import loadable from "@loadable/component"
import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { push } from "redux-first-history"

import { history } from "app-store-v2/index"
import { fetchShareEmail } from "app-store-v2/share/actions"
import AcceptPayments from "areas/cart/footer/accept-payments"
import ItemEditForm from "components/admin/item-edit-form"
import { PdpThresholdProgressBar } from "components/threshold-progress-bar"
import Button from "components/ui/button"
import Media from "components/ui/media"
import { IS_JEWLR, IS_PORTAL, retry } from "helpers/application"

import AddToCartButton from "./add-to-cart-button"
import OutOfStockForm from "./out-of-stock-form"
import ShippingInfo from "./shipping-info"
import SocialShare from "./social-share"
import ValueProps from "./value-props"
import { PDP_REVIEWS_BOX_TARGETED_PRODUCTS } from "./wizard-header/pdp-reviews-data"

const PdpReviewsCarousel = loadable(() =>
  retry(() => import("./wizard-header/pdp-reviews-carousel"))
)

const selectPreviewImage = createSelector(
  [
    (state) => state.previewImage?.data?.all_views,
    (state) => state.previewImage?.data?.images,
  ],
  (allViews = [], slides = {}) => {
    const previewSlide = Object.entries(slides).find(([view]) =>
      allViews.includes(view.replace("_view", ""))
    )
    return previewSlide ? previewSlide[1] : null
  }
)

const selectWizardActionsData = createSelector(
  [
    (state) => state.wizard?.adminMode,
    (state) => state.wizard?.formUrl,
    (state) => state.wizard?.header,
    (state) => state.wizard?.olid,
    (state) => state.wizard?.orderNumber,
    (state) => state.wizardUI?.prefStore,
    selectPreviewImage,
    (state) => state.choices?.style_code?.value,
    (state) => state.wizard?.loading,
  ],
  (
    adminMode,
    formUrl,
    header,
    olid,
    orderNumber,
    prefStore,
    previewImage,
    styleCode,
    wizardLoading
  ) => ({
    adminMode,
    formUrl,
    header,
    olid,
    orderNumber,
    prefStore,
    previewImage,
    styleCode,
    wizardLoading,
  })
)

const WizardActions = ({ imageAreaRef, outOfStockRef, wizardRef }) => {
  const dispatch = useDispatch()
  const {
    adminMode,
    formUrl,
    header,
    olid,
    orderNumber,
    prefStore,
    previewImage,
    styleCode,
    wizardLoading,
  } = useSelector(selectWizardActionsData)

  const shouldShowReviews = useMemo(
    () => PDP_REVIEWS_BOX_TARGETED_PRODUCTS.includes(styleCode),
    [styleCode]
  )

  const openShareModal = useCallback(() => {
    dispatch(fetchShareEmail())
    dispatch(
      push(`${history.location.pathname}${history.location.search}#share-modal`)
    )
  }, [dispatch])

  if (!header) return null

  if (header.product_out_of_stock) {
    return (
      <>
        <OutOfStockForm outOfStockRef={outOfStockRef} />
        <ShippingInfo />
      </>
    )
  } else if (olid) {
    return (
      <>
        <ItemEditForm />
        {adminMode ? (
          <Button
            className="text-neutral border-neutral text-18 w-full border disabled:border-none"
            disabled={!prefStore || wizardLoading}
            onClick={() => (window.location = `${formUrl}?cancel=true`)}
            rel="nofollow"
            variant="tertiary"
          >
            Cancel Change
          </Button>
        ) : (
          <Button
            asChild
            className="text-neutral border-neutral text-18 w-full border disabled:border-none"
            disabled={!prefStore || wizardLoading}
            variant="tertiary"
          >
            <Link
              data-lc="wizard-actions"
              rel="nofollow"
              to={`/order/change?order_number=${orderNumber}&lii=${olid}`}
            >
              Cancel Change
            </Link>
          </Button>
        )}
      </>
    )
  }

  return (
    <>
      <AddToCartButton
        imageAreaRef={imageAreaRef}
        previewImage={previewImage}
        wizardRef={wizardRef}
      />
      {IS_PORTAL() && <AcceptPayments />}
      {sl.config.sale_key.includes("2025_jewlr_days_flash") && (
        <div className="mb-16">
          <PdpThresholdProgressBar inPdp />
        </div>
      )}
      {IS_JEWLR() && (
        <>
          {shouldShowReviews && (
            <Media lessThan="tablet">
              <PdpReviewsCarousel />
            </Media>
          )}
          <Media greaterThanOrEqual="tablet">
            <ValueProps />
          </Media>
        </>
      )}
      <ShippingInfo />
      {IS_JEWLR() && (
        <div className="desktop:flex-col flex">
          <div className="flex-1">
            <Button
              className="tablet:border tablet:border-neutral text-primary tablet:text-black tablet:text-18 tablet:font-light tablet:no-underline mx-auto w-full disabled:border-none"
              data-cy="product-wizard-send-a-hint-btn"
              disabled={!prefStore || wizardLoading}
              onClick={() => openShareModal("product")}
              underline
              variant="tertiary"
            >
              Send a Hint
            </Button>
          </div>
          <SocialShare />
        </div>
      )}
    </>
  )
}

WizardActions.propTypes = {
  imageAreaRef: PropTypes.object,
  outOfStockRef: PropTypes.object,
  wizardRef: PropTypes.object,
}

export default WizardActions
